// src/components/dashboards/appBackdrop.tsx
// ---------------------------------------------------------------------------
// The animated backdrop used behind the app's glass surfaces.
//
// IN-APP IT IS A STILL FRAME, NOT THE VIDEO. Why:
//
//   - Every mounted <video> decodes its own stream; browsers do NOT share a
//     decode between elements. The dashboard alone references
//     VideoGlassContainer 9 times, so a clinician can have several 1280x720
//     30fps decoders running at once, all day, on battery.
//   - Those pixels are then covered by a 0.88 -> 0.72 alpha navy gradient AND
//     a blur(20px) glass card. Measured against the real overlay, the mean
//     RGB distance between the video frame and the still is 1.26, below the
//     ~2-3 just-noticeable threshold.
//   - A logged-in user who never opens the landing page was downloading
//     7.56 MB of video to look at a dashboard. The still is 49 KB.
//
// Occluding the video would NOT have helped: a covered <video> still decodes
// every frame. Only not rendering one removes the cost.
//
// The LANDING hero keeps the real video (HeroSection.tsx): there it is the
// centrepiece, barely overlaid, and loaded once by a visitor rather than
// continuously by a daily user.
// ---------------------------------------------------------------------------

import { Box } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";

export const HERO_BG_VIDEO = "/images/landing/hero_bg.mp4";
export const HERO_BG_POSTER = "/images/landing/hero_bg_poster.jpg";

/**
 * Flip to "video" to restore the animated backdrop in-app.
 * Single switch: every in-app surface reads it, the landing hero ignores it.
 */
export const IN_APP_BACKDROP: "still" | "video" = "still";

/** Absolutely-positioned backdrop filling its (position: relative) parent.
 *
 *  `sx` is passed through because two callers deliberately render this at
 *  `opacity: 0` ("retained for future use"). Those were paying FULL video
 *  decode for something 100% invisible; as a still they cost nothing, and the
 *  original intent is preserved rather than silently deleted. */
export default function AppBackdrop({ sx }: { sx?: SxProps<Theme> }) {
  const common = {
    position: "absolute" as const,
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover" as const,
    zIndex: 0,
  };

  if (IN_APP_BACKDROP === "video") {
    return (
      <Box
        component="video"
        autoPlay
        muted
        loop
        playsInline
        sx={{ ...common, ...sx }}
      >
        <source src={HERO_BG_VIDEO} type="video/mp4" />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...common,
        backgroundImage: `url(${HERO_BG_POSTER})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        ...sx,
      }}
    />
  );
}
